import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { TextField } from '@radix-ui/themes'
import { NavigateFn, useSearch } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import React from 'react'
import styled, { css } from 'styled-components'

import { ColumnConfiguration } from './ColumnConfiguration'
import { FilterBar } from './filters/FilterBar'

const StyledFilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
`

const FilterGroupStyles = css`
  display: flex;
  gap: 4px;
  height: 100%;
  &:empty {
    display: none;
  }
`
const FilterButtonGroup = styled.div`
  ${FilterGroupStyles}
  min-width: 0;
  width: 100%;
`

const SearchContainer = styled.div`
  ${FilterGroupStyles}
  margin-left: auto;
`

const StyledTextFieldRoot = styled(TextField.Root)`
  min-height: 2rem;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton} !important;
  }
`

const StyledTextFieldSlot = styled(TextField.Slot)`
  padding: 4px;
`

export const TableTopBar = <TData,>({
  columns,
  setGlobalFilter,
  getColumn,
  showFilterBar,
  showGlobalFilter,
  showColumnConfig,
  leadingContent,
  initialColumnFilters,
  navigate,
  clearAll,
  disableUrlFilters,
  defaultVisibleColumnIds,
}: {
  columns: ColumnDef<TData>[]
  setGlobalFilter: React.Dispatch<unknown>
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  showFilterBar: boolean
  showGlobalFilter: boolean
  showColumnConfig: boolean
  leadingContent?: React.ReactNode
  initialColumnFilters?: ColumnFiltersState
  navigate: NavigateFn
  clearAll?: () => void
  disableUrlFilters?: boolean
  defaultVisibleColumnIds?: string[]
}) => {
  const search = useSearch({ strict: false })
  const searchParam = search.search as string
  const [searchValue, setSearchValue] = React.useState(searchParam || '')
  const clearAllRef = React.useRef(clearAll)

  // Sync with URL on mount and URL changes
  React.useEffect(() => {
    setSearchValue(searchParam || '')
    setGlobalFilter(searchParam || '')
  }, [searchParam, setGlobalFilter])

  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    setSearchValue(value)
    setGlobalFilter(value)
    if (!disableUrlFilters) {
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          search: value || undefined,
        }),
      })
    }
  }

  React.useEffect(() => {
    if (clearAll) {
      clearAllRef.current = () => {
        clearAll()
        setSearchValue('')
        setGlobalFilter('')
        if (!disableUrlFilters) {
          navigate({
            to: '.',
            search: (prev) => ({
              ...prev,
              search: undefined,
            }),
          })
        }
      }
    }
  }, [clearAll, navigate, setGlobalFilter, disableUrlFilters])

  return (
    <StyledFilterContainer>
      {leadingContent ? (
        <FilterButtonGroup>{leadingContent}</FilterButtonGroup>
      ) : null}
      {columns.length > 0 && showFilterBar && (
        <FilterButtonGroup>
          <FilterBar
            columns={columns}
            getColumn={getColumn}
            initialColumnFilters={initialColumnFilters}
            navigate={navigate}
            disableUrlFilters={disableUrlFilters}
          />
        </FilterButtonGroup>
      )}
      {showGlobalFilter && (
        <SearchContainer>
          <StyledTextFieldRoot
            placeholder='Search'
            variant='soft'
            value={searchValue}
            onChange={handleFilterInputChange}>
            <StyledTextFieldSlot>
              <MagnifyingGlassIcon height='16' width='16' />
            </StyledTextFieldSlot>
          </StyledTextFieldRoot>
          {showColumnConfig && (
            <ColumnConfiguration
              columns={columns}
              getColumn={getColumn}
              defaultVisibleColumnIds={defaultVisibleColumnIds}
            />
          )}
        </SearchContainer>
      )}
    </StyledFilterContainer>
  )
}
